import type { LogRole } from "../../core/types.js";
import {
    DiscordPermissionFlags,
    parsePermissionBits,
    type DiscordPermissionFlag,
} from "../../logs/common/role/permissions.js";

export type StepId = "targets" | "model" | "permissions";
export type RolePickerMode = "targets" | "references";
export type DraftMode = "first-target" | "references" | "empty";

export type PendingConfirmation = {
    roleIds: string[];
    permissions: string;
    changes: PermissionChange[];
};

export type PermissionChange = {
    role: LogRole;
    before: bigint;
    after: bigint;
    changedPermissions: DiscordPermissionFlag[];
};

export const Steps = [
    { id: "targets", label: "Cibles", title: "Sélectionner les rôles cibles" },
    { id: "model", label: "Modèle", title: "Choisir une base de permissions" },
    { id: "permissions", label: "Permissions", title: "Ajuster les permissions" },
] as const satisfies readonly { id: StepId; label: string; title: string }[];

export function formatPermissionLabel(permission: DiscordPermissionFlag): string {
    return permission.key
        .toLowerCase()
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
}

export function getRoleColor(role: LogRole): string {
    const primary = role.colors?.primaryColor ?? role.colors?.primary_color ?? role.color;
    return primary ? `#${primary.toString(16).padStart(6, "0")}` : "#8b95a5";
}

export function getRoleBits(role: LogRole): bigint {
    return parsePermissionBits(role.permissions) ?? 0n;
}

export function sortRoles(roles: LogRole[]): LogRole[] {
    return [ ...roles ].sort((left, right) => {
        const positionDiff = (right.position ?? 0) - (left.position ?? 0);
        return positionDiff !== 0 ? positionDiff : right.id.localeCompare(left.id);
    });
}

export function combineReferencePermissions(roles: LogRole[], roleIds: string[]): bigint {
    const selected = new Set(roleIds);
    let result = 0n;

    for (const role of roles)
        if (selected.has(role.id))
            result |= getRoleBits(role);

    return result;
}

export function diffPermissions(before: bigint, after: bigint): DiscordPermissionFlag[] {
    return DiscordPermissionFlags.filter((permission) => {
        const wasEnabled = (before & permission.bit) !== 0n;
        const isEnabled = (after & permission.bit) !== 0n;
        return wasEnabled !== isEnabled;
    });
}
